import React, { useState, useRef, useEffect } from "react";

export default function FuturesTransferModal({
  open,
  onClose,
  isMargin,
  fromWalletType,
  setFromWalletType,
  toWalletType,
  setToWalletType,
  availableWalletTypes,
  selectedCurrency,
  setSelectedCurrency,
  transferAmount,
  setTransferAmount,
  transferable = "0",
  balanceLoading = false,
  handleInterchangeWallet,
  handleWalletTransfer,
  currencyData,
  transferCoinSearch,
  setTransferCoinSearch,
  fiveDecimal,
}) {
  const [isToDropdownOpen, setIsToDropdownOpen] = useState(false);
  const [isCoinSelectOpen, setIsCoinSelectOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsToDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!open) return null;

  const maxTransferable = parseFloat(transferable || 0);
  const amountValid =
    !balanceLoading &&
    transferAmount &&
    Number(transferAmount) > 0 &&
    Number(transferAmount) <= maxTransferable;

  const toOptions = [
    { label: "USDT Perp", value: "futures" },
    { label: "BTC Perp", value: "btc_perp" },
    { label: "CFD", value: "cfd", actionLabel: "Activate Now" },
    { label: "Delivery Futures", value: "delivery" },
    { label: "Isolated Margin", value: "isolated" },
    { label: "Options", value: "options" },
  ];

  const getToLabel = (val) => {
    const s = String(val || "");
    const opt = toOptions.find(o => o.value === s);
    return opt ? opt.label : s ? s.charAt(0).toUpperCase() + s.slice(1) : "Spot";
  };

  const dummyCoins = [
    { short_name: "BTC", full_name: "Bitcoin", image: "/images/bitcoin_icon.png", currency_id: 1 },
    { short_name: "USDT", full_name: "Tether", image: "/images/usdt_icon.png", currency_id: 2 },
    { short_name: "BNB", full_name: "Binance", image: "/images/bnb_icon.png", currency_id: 3 },
    { short_name: "TRX", full_name: "Tron", image: "/images/trx_icon.png", currency_id: 4 },
    { short_name: "ETH", full_name: "Ethereum", image: "/images/eth_icon.png", currency_id: 5 },
    { short_name: "AVAX", full_name: "Avalanche", image: "/images/sol_icon.png", currency_id: 6 },
    { short_name: "DOGE", full_name: "Dogecoin", image: "/images/xrp_icon.png", currency_id: 7 },
  ];

  const displayCoins = Array.isArray(currencyData) && currencyData.length > 0 ? currencyData : dummyCoins;

  const filteredCoins = displayCoins.filter(coin =>
    (coin.short_name || "").toLowerCase().includes((transferCoinSearch || "").toLowerCase()) ||
    (coin.full_name || "").toLowerCase().includes((transferCoinSearch || "").toLowerCase())
  );

  return (
    <div className="agcx-ft-modal-root" role="dialog" aria-modal="true">
      <button type="button" className="agcx-ft-modal-backdrop" aria-label="Close" onClick={onClose} />

      {!isCoinSelectOpen ? (
        /* Main Transfer Panel */
        <div className="agcx-ft-modal-panel agcx-ft-modal-panel--transfer">
          <div className="agcx-ft-modal-head">
            <h2 className="agcx-ft-modal-title">Funds Transfer</h2>
            <button type="button" className="agcx-ft-modal-close" onClick={onClose}>
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="agcx-ft-modal-body agcx-ft-transfer-body">
            <div className="agcx-ft-transfer-banner">
              <div className="banner-icon">
                <img src="/images/fund_account_vector.svg" alt="" />
              </div>
              <div className="banner-text">
                Upgrade to Unified Account & Trade with Shared Spot Margin
              </div>
              <button type="button" className="banner-close">
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="agcx-ft-transfer-row">
              {isMargin ? (
                <>
                  <div className="agcx-ft-transfer-field agcx-ft-transfer-field--from" ref={dropdownRef}>
                    <label>From</label>
                    <div
                      className={`agcx-ft-transfer-box is-select ${isToDropdownOpen ? "is-active" : ""}`}
                      onClick={() => setIsToDropdownOpen(!isToDropdownOpen)}
                    >
                      {getToLabel(fromWalletType)}
                      <i className={isToDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                    </div>
                    {isToDropdownOpen && (
                      <div className="agcx-ft-transfer-dropdown">
                        {toOptions.map((opt) => (
                          <div
                            key={opt.value}
                            className={`agcx-ft-transfer-dropdown-item ${fromWalletType === opt.value ? "is-selected" : ""}`}
                            onClick={() => {
                              setFromWalletType(opt.value);
                              setIsToDropdownOpen(false);
                            }}
                          >
                            <span className="item-label">{opt.label}</span>
                            {opt.actionLabel && <span className="item-action">{opt.actionLabel}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button type="button" className="agcx-ft-transfer-swap" onClick={handleInterchangeWallet}>
                    <img src="/images/account_roatate_vector.svg" alt="swap" />
                  </button>
                  <div className="agcx-ft-transfer-field agcx-ft-transfer-field--to">
                    <label>To</label>
                    <div className="agcx-ft-transfer-box">
                      {toWalletType === "spot" ? "Spot" : String(toWalletType || "").charAt(0).toUpperCase() + String(toWalletType || "").slice(1)}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="agcx-ft-transfer-field agcx-ft-transfer-field--from">
                    <label>From</label>
                    <div className="agcx-ft-transfer-box">
                      {fromWalletType === "spot" ? "Spot" : String(fromWalletType || "").charAt(0).toUpperCase() + String(fromWalletType || "").slice(1)}
                    </div>
                  </div>
                  <button type="button" className="agcx-ft-transfer-swap" onClick={handleInterchangeWallet}>
                    <img src="/images/account_roatate_vector.svg" alt="swap" />
                  </button>
                  <div className="agcx-ft-transfer-field agcx-ft-transfer-field--to" ref={dropdownRef}>
                    <label>To</label>
                    <div
                      className={`agcx-ft-transfer-box is-select ${isToDropdownOpen ? "is-active" : ""}`}
                      onClick={() => setIsToDropdownOpen(!isToDropdownOpen)}
                    >
                      {getToLabel(toWalletType)}
                      <i className={isToDropdownOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                    </div>
                    {isToDropdownOpen && (
                      <div className="agcx-ft-transfer-dropdown">
                        {toOptions.map((opt) => (
                          <div
                            key={opt.value}
                            className={`agcx-ft-transfer-dropdown-item ${toWalletType === opt.value ? "is-selected" : ""}`}
                            onClick={() => {
                              setToWalletType(opt.value);
                              setIsToDropdownOpen(false);
                            }}
                          >
                            <span className="item-label">{opt.label}</span>
                            {opt.actionLabel && <span className="item-action">{opt.actionLabel}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="agcx-ft-transfer-field">
              <label>Coin</label>
              <div
                className="agcx-ft-transfer-box is-coin is-select"
                onClick={() => setIsCoinSelectOpen(true)}
              >
                <div className="coin-info">
                  <img src={selectedCurrency?.image || "/images/bitcoin_icon.png"} alt="" onError={(e) => e.target.src = "/images/bitcoin_icon.png"} />
                  <span>{selectedCurrency?.short_name || "BTC"}</span>
                </div>
                <i className="ri-arrow-down-s-line"></i>
              </div>
              {isMargin && (
                <div className="agcx-ft-transfer-quick-coins">
                  {["0G", "USDT"].map((code) => {
                    const active = (selectedCurrency?.short_name || "").toUpperCase() === code;
                    return (
                      <button
                        key={code}
                        type="button"
                        className={`agcx-ft-quick-coin${active ? " is-active" : ""}`}
                        onClick={() => {
                          let match = displayCoins.find((c) => (c.short_name || "").toUpperCase() === code);
                          if (!match) {
                            match = { short_name: code, full_name: code, image: `/images/${code.toLowerCase()}_icon.png`, currency_id: code };
                          }
                          setSelectedCurrency(match);
                        }}
                      >
                        <span className={`agcx-ft-quick-coin-radio${active ? " is-checked" : ""}`} />
                        {code}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="agcx-ft-transfer-field">
              <label>Size</label>
              <div className="agcx-ft-transfer-input-wrap">
                <input
                  type="text"
                  placeholder="Please enter the amount"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                />
                <div className="input-suffix">
                  <span>{selectedCurrency?.short_name || "BTC"}</span>
                  <div className="divider"></div>
                  <button
                    type="button"
                    className="all-btn"
                    disabled={balanceLoading}
                    onClick={() => setTransferAmount(String(transferable || 0))}
                  >
                    All
                  </button>
                </div>
              </div>
              <div className="agcx-ft-transfer-avail">
                Transferrable:{" "}
                {balanceLoading ? (
                  <span className="agcx-ft-transfer-balance-skeleton" aria-hidden="true" />
                ) : (
                  <>
                    {fiveDecimal(parseFloat(transferable || 0))} {selectedCurrency?.short_name || "BTC"}
                  </>
                )}
              </div>
            </div>

            <div className="agcx-ft-transfer-actions">
              <button type="button" className="agcx-ft-btn agcx-ft-btn--ghost" onClick={onClose}>Cancel</button>
              <button
                type="button"
                className="agcx-ft-btn agcx-ft-btn--primary"
                disabled={!amountValid}
                onClick={handleWalletTransfer}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Select Crypto Panel */
        <div className="agcx-ft-modal-panel agcx-ft-modal-panel--coin-select">
          <div className="agcx-ft-modal-head">
            <h2 className="agcx-ft-modal-title">Select Crypto</h2>
            <button type="button" className="agcx-ft-modal-close" onClick={() => setIsCoinSelectOpen(false)}>
              <i className="ri-close-line"></i>
            </button>
          </div>
          <div className="agcx-ft-modal-body">
            <div className="agcx-ft-coin-search">
              <input
                type="text"
                placeholder="Search coin name"
                value={transferCoinSearch}
                onChange={(e) => setTransferCoinSearch(e.target.value)}
                autoFocus
              />
              <i className="ri-search-line"></i>
            </div>
            <div className="agcx-ft-coin-list">
              {filteredCoins.map((coin, idx) => (
                <div
                  key={coin.currency_id || idx}
                  className="agcx-ft-coin-item"
                  onClick={() => {
                    setSelectedCurrency(coin);
                    setIsCoinSelectOpen(false);
                    setTransferCoinSearch("");
                  }}
                >
                  <div className="coin-item-left">
                    <img src={coin.image || "/images/bitcoin_icon.png"} alt="" onError={(e) => e.target.src = "/images/bitcoin_icon.png"} />
                    <div className="coin-item-info">
                      <span className="coin-short">{coin.short_name}</span>
                      <span className="coin-full">{coin.full_name || ""}</span>
                    </div>
                  </div>
                  {selectedCurrency?.short_name === coin.short_name && <i className="ri-check-line text-primary"></i>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
